import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  LogIn,
  Menu,
  X,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { LOGO, getUser, api } from "../lib/api";
import { useSiteContent } from "../lib/siteContent";

const slugOf = (to = "") => (String(to).replace(/^https?:\/\/[^/]+/, "").replace(/^\//, "").split(/[/?#]/)[0] || "home").replace(/[^a-z0-9-]/gi, "");

// Fetch branding once & cache. Any Header/Footer mount reads latest.
let _brandingCache = null;
let _brandingListeners = [];
function useBranding() {
  const [b, setB] = useState(_brandingCache);
  useEffect(() => {
    if (_brandingCache) {
      setB(_brandingCache);
      return;
    }
    _brandingListeners.push(setB);
    if (_brandingListeners.length === 1) {
      api("/settings/branding")
        .then((res) => {
          _brandingCache = res;
          _brandingListeners.forEach((l) => l(res));
          _brandingListeners = [];
        })
        .catch(() => {});
    }
  }, []);
  return b;
}

function extractBrandTitle(fullTitle) {
  if (!fullTitle) return "MASTOFA.COM";
  // Split on em-dash / hyphen, take the first part
  return fullTitle.split(/[—–-]/)[0].trim() || "MASTOFA.COM";
}

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const user = getUser();
  const nav = useNavigate();
  const branding = useBranding();
  const { t, list } = useSiteContent("nav");
  const logoUrl = branding?.logo_url || LOGO;
  const brandTitle = extractBrandTitle(branding?.site_title);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Body scroll lock while drawer is open (mobile) + ESC closes
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <header className="header">
      <Link to="/" className="brand" data-testid="brand-logo">
        <img src={logoUrl} alt={brandTitle} />
        <span data-testid="brand-title">
          {brandTitle.includes(".") ? (
            <>
              {brandTitle.split(".")[0]}
              <span className="dot">.</span>
              {brandTitle.split(".").slice(1).join(".")}
            </>
          ) : (
            brandTitle
          )}
        </span>
      </Link>
      <button
        className="mobile-menu"
        data-testid="mobile-menu-button"
        onClick={() => setOpen(!open)}
        aria-label={open ? "Tutup menu" : "Buka menu"}
        aria-expanded={open}
      >
        {open ? <X /> : <Menu />}
      </button>
      {/* Backdrop for mobile drawer */}
      {open && (
        <button
          className="nav-backdrop"
          data-testid="nav-backdrop"
          aria-label="Tutup menu"
          onClick={() => setOpen(false)}
        />
      )}
      <nav className={open ? "nav open" : "nav"}>
        {list("links.items").filter((l) => l.visible !== false && l.to).map((l) => (
          <Link
            key={l.to + l.label}
            data-testid={`nav-${slugOf(l.to)}-link`}
            to={l.to}
            className={isActive(l.to) ? "nav-link active" : "nav-link"}
            aria-current={isActive(l.to) ? "page" : undefined}
          >
            {l.label}
          </Link>
        ))}
        {user?.id ? (
          <button
            className="button button-small nav-cta"
            data-testid="nav-dashboard-button"
            onClick={() => {
              setOpen(false);
              nav(user.role === "ADMIN" ? "/admin" : "/dashboard");
            }}
          >
            {t("cta.dashboard")} <ArrowRight size={16} />
          </button>
        ) : (
          <>
            <Link
              data-testid="nav-login-link"
              to="/login"
              className="nav-login"
            >
              <LogIn size={16} /> {t("cta.login")}
            </Link>
            <Link
              data-testid="nav-start-link"
              to={t("cta.start_to")}
              className="button button-small nav-cta"
            >
              {t("cta.start")} <ArrowRight size={16} />
            </Link>
          </>
        )}
      </nav>
    </header>
  );
}

export function Footer() {
  const branding = useBranding();
  const { t, list } = useSiteContent("footer");
  const logoUrl = branding?.logo_url || LOGO;
  const brandTitle = extractBrandTitle(branding?.site_title);
  const FLink = ({ l }) => (/^https?:/.test(l.to) ? <a href={l.to} target="_blank" rel="noreferrer" data-testid={`footer-${slugOf(l.to)}`}>{l.label}</a> : <Link to={l.to} data-testid={`footer-${slugOf(l.to)}`}>{l.label}</Link>);
  return (
    <footer className="site-footer">
      <div className="footer-main">
        <div className="footer-col footer-brand-col">
          <Link to="/" className="brand">
            <img src={logoUrl} alt={brandTitle} />
            <span>{brandTitle}</span>
          </Link>
          <p className="footer-tagline">{t("brand.tagline")}</p>
          <p className="footer-addr">
            {String(t("brand.address")).split("\n").map((ln, i) => (<span key={i}>{i > 0 && <br />}{ln}</span>))}
          </p>
          <a
            href={t("brand.wa_link")}
            target="_blank" rel="noreferrer"
            className="footer-wa"
            data-testid="footer-wa"
          >
            <ShieldCheck size={14} /> {t("brand.wa_label")}
          </a>
        </div>

        {["col1", "col2", "col3"].map((c) => (
          <div className="footer-col" key={c}>
            <h4>{t(`${c}.title`)}</h4>
            {list(`${c}.items`).filter((l) => l.to && l.label).map((l) => <FLink key={l.to + l.label} l={l} />)}
          </div>
        ))}
      </div>
      <div className="footer-bottom">
        <span>{t("bottom.copyright")}</span>
        <span>{t("bottom.motto")}</span>
      </div>
    </footer>
  );
}

export function CourseCard({ course, index = 0 }) {
  return (
    <Link
      to={`/courses/${course.slug}`}
      className="course-card"
      data-testid={`course-card-${course.id}`}
    >
      <div className="course-image">
        <img src={course.thumbnail} alt={course.title} />
        <span className="course-index">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>
      <div className="course-info">
        <div className="course-meta">
          <span>{course.level}</span>
          <span>{course.category}</span>
        </div>
        <h3>{course.title}</h3>
        <div className="course-bottom">
          <b>Rp {Number(course.price || 0).toLocaleString("id-ID")}</b>
          <span className="circle-arrow">
            <ArrowRight size={17} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function Loading({ label = "Memuat..." }) {
  return (
    <div className="loading" data-testid="loading-state">
      {label}
    </div>
  );
}

export function Empty({ icon, title, body, cta }) {
  return (
    <div className="empty" data-testid="empty-state">
      {icon}
      <b>{title}</b>
      <p>{body}</p>
      {cta}
    </div>
  );
}

export { ShieldCheck, CheckCircle2, ArrowRight };
